import { useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Button,
  toastManager,
} from "@salestrak/ui";

import { showUndoToast, UNDO_TOAST_TIMEOUT_MS } from "#app/lib/undo-shortcut";
import type { Form, FormSubmission } from "#app/lib/forms";
import { formatDateShort } from "#app/lib/utils/format-date";

type SubmissionStore = {
  deleteSubmission: (id: string) => Promise<{ deleted: boolean; undoable: boolean }>;
  restoreSubmission: (id: string) => Promise<void>;
};

// A filling with photos asks first: its photos are deleted with it, so there is no Undo.
export function useDeleteSubmission(
  store: SubmissionStore,
  onDeleted?: (row: FormSubmission) => void,
) {
  const [asking, setAsking] = useState<FormSubmission | null>(null);
  const photoIdsOf = (row: FormSubmission) => row.media.map((media) => media.id);
  const run = async (row: FormSubmission) => {
    const { deleted, undoable } = await store.deleteSubmission(row.submissionId);
    if (!deleted) return;
    onDeleted?.(row);
    const title = row.status === "submitted" ? "Submission deleted" : "Filling deleted";
    if (!undoable) {
      toastManager.add({ title, timeout: UNDO_TOAST_TIMEOUT_MS });
      return;
    }
    showUndoToast({ title, onUndo: () => void store.restoreSubmission(row.submissionId) });
  };
  const request = (row: FormSubmission) =>
    row.status === "submitted" || !row.viewerCanOpen || photoIdsOf(row).length > 0 ? setAsking(row) : void run(row);
  const confirm = (
    <AlertDialog
      open={asking !== null}
      onOpenChange={(open) => {
        if (!open) setAsking(null);
      }}
    >
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>{asking?.status === "inProgress" ? "Delete filling?" : "Delete submission?"}</AlertDialogTitle>
          <AlertDialogDescription>
            {asking?.status === "inProgress"
              ? "Its answers are deleted for everyone."
              : "It's removed from this Project for everyone, along with its answers."}
            {asking && !asking.viewerCanOpen
              ? " Also deletes any photos it holds from Attachments, which can't be undone."
              : asking && photoIdsOf(asking).length > 0
                ? ` Also deletes its ${photosLabel(photoIdsOf(asking).length)} from Attachments.`
                : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
          <Button
            color="destructive"
            onClick={() => {
              if (asking) void run(asking);
              setAsking(null);
            }}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
  return { request, confirm };
}

function photosLabel(count: number): string {
  return count === 1 ? "photo" : `${count} photos`;
}

export function formPhotoDeleteMessage(form: Pick<Form, "name">, submission: FormSubmission): string {
  return `Deletes it from this Project for everyone. Also removes it from the ${formatDateShort(submission.submittedAt)} ${form.name} submission.`;
}
